import { Component, OnInit, TemplateRef } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { API } from 'src/environments/environment';

interface PlanoContaTreeNode {
  id: number;
  codigo: string;
  nome: string;
  tipo: string;
  natureza: string;
  contaPaiId?: number | null;
  ativo?: boolean;
  filhos: PlanoContaTreeNode[];
}

@Component({
  selector: 'app-plano-contas-tree',
  templateUrl: './plano-contas-tree.component.html',
  styleUrls: ['./plano-contas-tree.component.scss']
})
export class PlanoContasTreeComponent implements OnInit {
  arvore: PlanoContaTreeNode[] = [];
  listaPlana: PlanoContaTreeNode[] = [];
  expandido: { [id: number]: boolean } = {};
  modalRef: NgbModalRef | null = null;

  editandoId = 0;
  codigo = '';
  nome = '';
  tipo: 'SINTETICA' | 'ANALITICA' = 'ANALITICA';
  natureza: 'ENTRADA' | 'SAIDA' = 'ENTRADA';
  contaPaiId: number | null = null;
  ativo = true;

  constructor(
    private ajaxService: AjaxService,
    private util: UtilService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.carregarArvore();
    this.carregarListaPlana();
  }

  get contasPaiDisponiveis(): PlanoContaTreeNode[] {
    return this.listaPlana
      .filter((x) => this.isTipoSintetica(x.tipo) && x.id !== this.editandoId)
      .sort((a, b) => (a.codigo || '').localeCompare((b.codigo || ''), undefined, { numeric: true }));
  }

  carregarArvore(): void {
    this.util.showLoading();
    this.ajaxService.get(`${API}financeiro/planocontas/arvore`).subscribe({
      next: (response) => {
        const data = this.extrairLista(response)
          .map((x: any) => this.normalizarNo(x))
          .filter((x: PlanoContaTreeNode) => x.id > 0);

        if (data.length > 0) {
          this.util.closeLoading();
          this.arvore = data;
          this.expandido = {};
          this.marcarExpandido(this.arvore);
          return;
        }

        this.ajaxService.get(`${API}financeiro/planocontas`).subscribe({
          next: (responseFlat) => {
            this.util.closeLoading();
            const flat = this.extrairLista(responseFlat)
              .map((x: any) => this.normalizarNo(x))
              .filter((x: PlanoContaTreeNode) => x.id > 0);

            this.arvore = this.montarArvore(flat);
            this.expandido = {};
            this.marcarExpandido(this.arvore);
          },
          error: () => {
            this.util.closeLoading();
            this.util.toasterShowError('Erro', 'Não foi possível carregar a árvore.');
          }
        });
      },
      error: () => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Não foi possível carregar a árvore.');
      }
    });
  }

  carregarListaPlana(onSuccess?: () => void, onError?: () => void): void {
    this.ajaxService.get(`${API}financeiro/planocontas`).subscribe({
      next: (responseFlat) => {
        this.listaPlana = this.extrairLista(responseFlat)
          .map((x: any) => this.normalizarNo(x))
          .filter((x: PlanoContaTreeNode) => x.id > 0);
        if (onSuccess) onSuccess();
      },
      error: () => {
        if (onError) onError();
      }
    });
  }

  hasFilhos(node: PlanoContaTreeNode): boolean {
    return !!node?.filhos?.length;
  }

  isExpandido(node: PlanoContaTreeNode): boolean {
    return this.expandido[node.id] !== false;
  }

  toggle(node: PlanoContaTreeNode): void {
    this.expandido[node.id] = !this.isExpandido(node);
  }

  novoRaiz(modal: TemplateRef<any>): void {
    this.prepararModal({
      editandoId: 0,
      codigo: '',
      nome: '',
      tipo: 'ANALITICA',
      natureza: 'ENTRADA',
      contaPaiId: null,
      ativo: true
    }, modal);
  }

  novoFilho(node: PlanoContaTreeNode, modal: TemplateRef<any>): void {
    if (!this.isTipoSintetica(node.tipo)) {
      this.util.toasterShowError('Atenção', 'Só é permitido adicionar filho em conta SINTETICA.');
      return;
    }

    this.prepararModal({
      editandoId: 0,
      codigo: '',
      nome: '',
      tipo: 'ANALITICA',
      natureza: this.normalizarNatureza(node.natureza),
      contaPaiId: node.id,
      ativo: true
    }, modal);
  }

  editar(node: PlanoContaTreeNode, modal: TemplateRef<any>): void {
    this.prepararModal({
      editandoId: node.id,
      codigo: node.codigo || '',
      nome: node.nome || '',
      tipo: this.normalizarTipo(node.tipo),
      natureza: this.normalizarNatureza(node.natureza),
      contaPaiId: node.contaPaiId ?? null,
      ativo: node.ativo !== false
    }, modal);
  }

  salvar(): void {
    if (!this.codigo.trim()) {
      this.util.toasterShowError('Atenção', 'Código é obrigatório.');
      return;
    }
    if (!this.nome.trim()) {
      this.util.toasterShowError('Atenção', 'Nome é obrigatório.');
      return;
    }
    if (this.contaPaiId && this.contaPaiId === this.editandoId) {
      this.util.toasterShowError('Atenção', 'A conta não pode ser pai dela mesma.');
      return;
    }

    const payload = {
      codigo: this.codigo.trim(),
      Codigo: this.codigo.trim(),
      nome: this.nome.trim(),
      Nome: this.nome.trim(),
      tipo: this.tipo,
      Tipo: this.tipo,
      natureza: this.natureza,
      Natureza: this.natureza,
      contaPaiId: this.contaPaiId,
      ContaPaiId: this.contaPaiId,
      ativo: this.ativo,
      Ativo: this.ativo
    };

    this.util.showLoading();
    const request = this.editandoId > 0
      ? this.ajaxService.put(`${API}financeiro/planocontas/${this.editandoId}`, payload)
      : this.ajaxService.post(`${API}financeiro/planocontas`, payload);

    request.subscribe({
      next: (response) => {
        this.util.closeLoading();
        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response.message || 'Não foi possível salvar.');
          return;
        }
        this.util.toasterShowSuccess('Sucesso', this.editandoId > 0 ? 'Conta atualizada com sucesso!' : 'Conta cadastrada com sucesso!');
        this.modalRef?.close();
        this.carregarArvore();
        this.carregarListaPlana();
      },
      error: () => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Erro ao salvar conta.');
      }
    });
  }

  remover(node: PlanoContaTreeNode): void {
    this.util.showLoading();
    this.ajaxService.delete(`${API}financeiro/planocontas/${node.id}`).subscribe({
      next: (response) => {
        this.util.closeLoading();
        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response.message || 'Não foi possível remover.');
          return;
        }
        this.util.toasterShowSuccess('Sucesso', 'Conta removida com sucesso!');
        this.carregarArvore();
        this.carregarListaPlana();
      },
      error: () => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Erro ao remover conta.');
      }
    });
  }

  private marcarExpandido(nodes: PlanoContaTreeNode[]): void {
    (nodes || []).forEach((node) => {
      this.expandido[node.id] = true;
      if (node.filhos?.length) this.marcarExpandido(node.filhos);
    });
  }

  private normalizarNo(item: any): PlanoContaTreeNode {
    const obj = this.util.converterParaLowercase(item || {});
    return {
      id: Number(obj.id || 0),
      codigo: String(obj.codigo || ''),
      nome: String(obj.nome || ''),
      tipo: String(obj.tipo || ''),
      natureza: String(obj.natureza || ''),
      contaPaiId: obj.contapaiid != null ? Number(obj.contapaiid) : null,
      ativo: obj.ativo === undefined ? true : Boolean(obj.ativo),
      filhos: Array.isArray(obj.filhos) ? obj.filhos.map((x: any) => this.normalizarNo(x)) : []
    };
  }

  private normalizarTipo(valor: any): 'SINTETICA' | 'ANALITICA' {
    const texto = this.normalizarTexto(String(valor || ''));
    return texto === 'SINTETICA' || texto === 'S' ? 'SINTETICA' : 'ANALITICA';
  }

  isTipoSintetica(valor: any): boolean {
    return this.normalizarTipo(valor) === 'SINTETICA';
  }

  isNaturezaEntrada(valor: any): boolean {
    return this.normalizarNatureza(valor) === 'ENTRADA';
  }

  classeMarcador(item: PlanoContaTreeNode): string {
    return this.isNaturezaEntrada(item.natureza) ? 'marker-entrada' : 'marker-saida';
  }

  classeTexto(item: PlanoContaTreeNode): string {
    return this.isTipoSintetica(item.tipo) ? 'text-sintetica-destaque' : 'text-neutro';
  }

  private normalizarNatureza(valor: any): 'ENTRADA' | 'SAIDA' {
    const texto = this.normalizarTexto(String(valor || ''));
    return texto === 'SAIDA' || texto === 'S' ? 'SAIDA' : 'ENTRADA';
  }

  private normalizarTexto(valor: string): string {
    return (valor || '')
      .trim()
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private prepararModal(
    dados: {
      editandoId: number;
      codigo: string;
      nome: string;
      tipo: 'SINTETICA' | 'ANALITICA';
      natureza: 'ENTRADA' | 'SAIDA';
      contaPaiId: number | null;
      ativo: boolean;
    },
    modal: TemplateRef<any>
  ): void {
    this.util.showLoading();
    this.carregarListaPlana(
      () => {
        this.util.closeLoading();
        this.editandoId = dados.editandoId;
        this.codigo = dados.codigo;
        this.nome = dados.nome;
        this.tipo = dados.tipo;
        this.natureza = dados.natureza;
        this.contaPaiId = dados.contaPaiId;
        this.ativo = dados.ativo;
        this.modalRef = this.modalService.open(modal, { size: 'lg', backdrop: 'static' });
      },
      () => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Não foi possível carregar dados para abrir o cadastro.');
      }
    );
  }

  private montarArvore(lista: PlanoContaTreeNode[]): PlanoContaTreeNode[] {
    const map = new Map<number, PlanoContaTreeNode>();
    const raizes: PlanoContaTreeNode[] = [];

    lista.forEach((x) => {
      map.set(x.id, { ...x, filhos: [] });
    });

    map.forEach((no) => {
      if (no.contaPaiId && map.has(no.contaPaiId)) {
        map.get(no.contaPaiId)!.filhos.push(no);
      } else {
        raizes.push(no);
      }
    });

    const ordenar = (nos: PlanoContaTreeNode[]) => {
      nos.sort((a, b) => (a.codigo || '').localeCompare((b.codigo || ''), undefined, { numeric: true }));
      nos.forEach((n) => ordenar(n.filhos || []));
    };
    ordenar(raizes);

    return raizes;
  }

  private extrairLista(response: any): any[] {
    let atual = response;
    for (let i = 0; i < 3; i++) {
      if (Array.isArray(atual)) break;
      if (atual && typeof atual === 'object' && Object.prototype.hasOwnProperty.call(atual, 'data') && atual.data != null) {
        atual = atual.data;
      } else {
        break;
      }
    }
    if (Array.isArray(atual)) return atual;
    if (atual && typeof atual === 'object') return Object.values(atual).filter((x: any) => x && typeof x === 'object');
    return [];
  }
}
